import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../services/config/firebase";
import { useEffect, useState } from "react";
import { AuthContext } from "./AuthContext";
export type { AuthContextType } from "./AuthContext";
import { getUserFromAzure } from "../services/AuthenticationService";
import type { HomeownerModel, StaffModel } from "../types/models";
import * as Sentry from "@sentry/react";
import { captureAppError, captureAppMessage } from "../observability/sentry";
import {
  CHECKOUT_SESSION_OWNER_KEY,
  clearCheckoutSession,
} from "../constants/bookingForm";
import {
  OAUTH_POPUP_PENDING_KEY,
  OAUTH_REDIRECT_PENDING_KEY,
} from "../constants/authStorage";
import { clearUtmAttribution } from "../utils/utmAttribution";
import { isForeignOwner, rememberOwner } from "../utils/sessionOwner";
import { clearAllUserCaches } from "../utils/cacheUtils";

// AuthContextType and AuthContext are now in ./AuthContext.ts
// (kept separate to avoid pulling Firebase into test utility imports)

export default function AuthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [user, setUser] = useState<HomeownerModel | StaffModel | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  async function refreshUser() {
    setLoading(true);
    const response = await getUserFromAzure();
    if (response && !response.error && response.role) {
      setUser(response.user);
      setRole(response.role);
    } else {
      setUser(null);
      setRole(null);
    }
    setLoading(false);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      if (firebaseUser) {
        try {
          /*
           * Block unverified email/password accounts before any network calls.
           * OAuth providers always set emailVerified=true, so this only affects
           * users who signed up with email+password and haven't verified yet.
           *
           * ⚠️ A PROVEN PHONE IS ALSO A VERIFIED CREDENTIAL (KAN-622 §4).
           *
           * Without the phone clause this signs out a customer who is legitimately signed in by
           * phone, the moment they add an email and a password: linking the password credential
           * puts "password" in `providerData`, the new address is not verified yet, and this gate
           * runs client-side BEFORE any request -- so they are ejected from a session they hold
           * by a credential this check never looked at.
           *
           * The backend already agrees: `authHomeowner` admits a phone-verified session via the
           * `phone_verified` custom claim (KAN-620). This is the other half.
           *
           * The email-first rule is untouched: no phone provider means the condition is exactly
           * what it was.
           */
          const isEmailPassword = firebaseUser.providerData.some(
            (p) => p.providerId === "password",
          );
          const hasVerifiedPhone = Boolean(
            firebaseUser.phoneNumber ||
              firebaseUser.providerData.some((p) => p.providerId === "phone"),
          );
          if (isEmailPassword && !firebaseUser.emailVerified && !hasVerifiedPhone) {
            setUser(null);
            setRole(null);
            await auth.signOut();
            setLoading(false);
            return;
          }

          // refresh token if needed
          await firebaseUser.getIdToken();
          // fetch user from Azure
          const response = await getUserFromAzure();

          if (response && !response.error && response.role) {
            /*
             * Keyed on the Firebase UID, and it fails CLOSED (KAN-623).
             *
             * This read `response.user?.email` and guarded both branches on it being truthy. A
             * phone-first homeowner has no email, so neither branch ran: the mismatch check
             * silently no-opped instead of clearing, and the incoming person INHERITED the
             * previous person's checkout draft and UTM attribution. The owner key was never
             * rewritten either, so the next person compared against a stale owner.
             *
             * See `isForeignOwner` for why the UID removes the need for those guards entirely.
             */
            const incomingOwner = response.user?.firebase_id;
            const storedOwner = sessionStorage.getItem(CHECKOUT_SESSION_OWNER_KEY);
            if (isForeignOwner(storedOwner, incomingOwner)) {
              clearCheckoutSession();
              clearUtmAttribution();
            }
            rememberOwner(CHECKOUT_SESSION_OWNER_KEY, incomingOwner);
            setUser(response.user);
            setRole(response.role);
          } else {
            // If an OAuth popup or redirect flow is in progress the DB record
            // may not exist yet — do NOT sign out; the flow will call
            // refreshUser() once the record is saved.
            const isOAuthPending =
              sessionStorage.getItem(OAUTH_POPUP_PENDING_KEY) === "1" ||
              sessionStorage.getItem(OAUTH_REDIRECT_PENDING_KEY) === "1";
            if (isOAuthPending) {
              setUser(null);
              setRole(null);
            } else {
              captureAppMessage("Failed to get Azure user", {
                area: "auth-provider",
                action: "get-azure-user",
                extra: { has_error: Boolean(response?.error) },
              });
              setUser(null);
              setRole(null);
              await auth.signOut();
            }
          }
        } catch (err) {
          const msg = String((err as Error)?.message ?? "");
          if (msg.includes("Connection to Indexed Database server lost")) {
            sessionStorage.setItem("session_interrupted", "idb");
          } else {
            captureAppError(err, {
              area: "auth-provider",
              action: "auth-state-changed",
            });
          }
          setUser(null);
          setRole(null);
          await auth.signOut();
        }
      } else {
        setUser(null);
        setRole(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: String(user.id),
      });

      Sentry.setContext("auth", {
        role,
      });
    } else {
      // FE-3: clear all user-scoped caches on sign-out so the next user never sees stale data
      clearAllUserCaches();
      Sentry.setUser(null);
      Sentry.setContext("auth", null);
    }
  }, [user, role]);

  return (
    <AuthContext.Provider value={{ user, role, loading, setUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
// Re-exported from its new home so the existing imports across the app keep working. New code
// that only reads auth state should import from ./AuthContext directly and stay clear of Firebase.
export { useAuth } from "./AuthContext";
